import { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { Trophy, FilePdf, ArrowsClockwise, Receipt, CheckCircle } from "@phosphor-icons/react";

const STATUSES = [
  { v: "reserved", label: "Réservée", color: "bg-amber-100 text-amber-900" },
  { v: "paid", label: "Payée", color: "bg-green-100 text-green-900" },
  { v: "invoiced", label: "Facturée", color: "bg-blue-100 text-blue-900" },
  { v: "cancelled", label: "Annulée", color: "bg-red-100 text-red-900" },
];

export default function CommanditesAdmin() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/sponsorships/admin/orders");
      setOrders(r.data?.orders || []);
      setStats(r.data?.stats || {});
    } catch {
      toast.error("Erreur de chargement.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/sponsorships/admin/orders/${id}`, { status });
      toast.success(`Statut → ${status}`);
      await reload();
    } catch {
      toast.error("Erreur.");
    }
  };

  return (
    <div className="space-y-6" data-testid="commandites-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-black text-3xl">Commandites Gala</h1>
          <p className="text-sm text-muted-foreground">Page publique : <a href="/commandites" target="_blank" rel="noreferrer" className="underline">/commandites</a></p>
        </div>
        <button onClick={reload} className="hard-border px-3 py-2 hover:bg-muted flex items-center gap-2 ticker text-sm" data-testid="reload-btn">
          <ArrowsClockwise size={16} /> Actualiser
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Trophy} label="Commandites" value={stats.total || 0} />
        <Stat icon={CheckCircle} label="Payées" value={stats.paid_count || 0} />
        <Stat icon={Receipt} label="Revenus HT" value={`${(stats.revenue_paid_ht || 0).toFixed(2)} $`} />
        <Stat icon={Receipt} label="Total TTC" value={`${(stats.revenue_paid_ttc || 0).toFixed(2)} $`} />
      </div>

      {/* Tax tracking */}
      {(stats.tps_collected || stats.tvq_collected) ? (
        <div className="hard-border bg-amber-50 p-4 text-sm">
          <div className="font-bold mb-2">📊 TPS/TVQ perçues à remettre</div>
          <div className="grid grid-cols-2 gap-4">
            <div>TPS collectée : <strong className="tabular-nums">{(stats.tps_collected || 0).toFixed(2)} $</strong></div>
            <div>TVQ collectée : <strong className="tabular-nums">{(stats.tvq_collected || 0).toFixed(2)} $</strong></div>
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Chargement…</div>
      ) : (
        <div className="hard-border bg-white" data-testid="orders-table">
          {orders.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">Aucune commandite pour l'instant.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="hard-border-b bg-muted/30 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">N°</th>
                  <th className="px-3 py-2 text-left">Compagnie</th>
                  <th className="px-3 py-2 text-left">Niveau</th>
                  <th className="px-3 py-2 text-right">Montant TTC</th>
                  <th className="px-3 py-2 text-left">Statut</th>
                  <th className="px-3 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id} className="hard-border-b" data-testid={`order-${o.order_no}`}>
                    <td className="px-3 py-2 font-mono">{o.order_no}</td>
                    <td className="px-3 py-2">
                      <div className="font-bold">{o.sponsor_company}</div>
                      <div className="text-xs text-muted-foreground">{o.sponsor_contact_name}</div>
                      <div className="text-xs text-muted-foreground">{o.sponsor_email}</div>
                    </td>
                    <td className="px-3 py-2">{o.plan_tier}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold">{(o.total_amount || 0).toFixed(2)} $</td>
                    <td className="px-3 py-2">
                      <span className={`ticker text-xs px-2 py-0.5 ${(STATUSES.find(s => s.v === o.status) || {}).color || ""}`}>
                        {(STATUSES.find(s => s.v === o.status) || {}).label || o.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 flex items-center gap-1">
                      <select
                        value={o.status}
                        onChange={e => updateStatus(o.id, e.target.value)}
                        className="hard-border bg-white px-2 py-1 text-xs"
                        data-testid={`status-${o.order_no}`}
                      >
                        {STATUSES.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
                      </select>
                      {(o.status === "paid" || o.status === "invoiced") && (
                        <a
                          href={`${API}/sponsorships/receipt/${o.id}.pdf`}
                          target="_blank"
                          rel="noreferrer"
                          className="hard-border px-2 py-1 hover:bg-muted text-xs flex items-center gap-1"
                          data-testid={`pdf-${o.order_no}`}
                        >
                          <FilePdf size={12} /> Reçu
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="hard-border bg-white p-4">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon size={16} />
        <div className="ticker text-xs">{label}</div>
      </div>
      <div className="font-display font-black text-2xl tabular-nums mt-1">{value}</div>
    </div>
  );
}
